import React, { useState } from 'react';
import { Brain, Lock, User, Mail, Check, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { register } from './authService';
import { useDesignSystem, cn } from './designSystem';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Registration = ({ onRegisterSuccess, onSwitchToLogin }) => {
  const { typography, componentStyles, effects } = useDesignSystem();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userType, setUserType] = useState('demo');

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Form validation
    if (!username || !email || !password || !confirmPassword) {
      setError('Alle feltene må fylles ut');
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passordene samsvarer ikke');
      setIsLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Ugyldig e-postadresse');
      setIsLoading(false);
      return;
    }

    // Password strength (minimum 8 characters)
    if (password.length < 8) {
      setError('Passordet må være minst 8 tegn');
      setIsLoading(false);
      return;
    }

    try {
      // Call the register function from authService
      const userData = await register(username, email, password, userType);
      
      // If successful, call the success callback
      onRegisterSuccess(userData);
    } catch (err) {
      setError(err.message || 'Kunne ikke registrere. Vennligst prøv igjen senere.');
      console.error('Registration error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 p-4">
      <Card className={cn(
        componentStyles.card.glass,
        "w-full max-w-md border-none shadow-lg"
      )}>
        <CardHeader className="pb-4 text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-4 rounded-xl">
              <Brain className="h-8 w-8 text-white" />
            </div>
          </div>
          <CardTitle className={typography.gradient + " text-2xl font-bold"}>
            Opprett Skribo-konto
          </CardTitle>
          <p className="text-sm text-blue-600/80 dark:text-blue-400/60 italic">
            Spar tid med smartere journalføring
          </p>
        </CardHeader>
        
        <CardContent>
          <Tabs defaultValue="demo" className="mb-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger 
                value="demo" 
                onClick={() => setUserType('demo')}
                className={cn(
                  "data-[state=active]:bg-gradient-to-r",
                  "data-[state=active]:from-blue-500",
                  "data-[state=active]:to-indigo-500",
                  "data-[state=active]:text-white"
                )}
              >
                Demo-konto
              </TabsTrigger>
              <TabsTrigger 
                value="regular"
                onClick={() => setUserType('demo')}
                className={cn(
                  "data-[state=active]:bg-gradient-to-r",
                  "data-[state=active]:from-blue-500",
                  "data-[state=active]:to-indigo-500",
                  "data-[state=active]:text-white"
                )}
              >
                Full tilgang
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="demo" className="mt-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg mb-6">
                <h3 className="font-medium mb-2">Demo-konto gir deg:</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <Check className="mr-2 h-4 w-4 text-blue-600 mt-0.5" />
                    <span>10 minutter med transkripsjon</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="mr-2 h-4 w-4 text-blue-600 mt-0.5" />
                    <span>Generer opptil 3 notater</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="mr-2 h-4 w-4 text-blue-600 mt-0.5" />
                    <span>Tilgang til standardmaler</span>
                  </li>
                </ul>
              </div>
            </TabsContent>
            
            <TabsContent value="regular" className="mt-4">
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg mb-6">
                <h3 className="font-medium mb-2">Full tilgang gir deg:</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <Check className="mr-2 h-4 w-4 text-green-600 mt-0.5" />
                    <span>Ubegrenset transkripsjon</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="mr-2 h-4 w-4 text-green-600 mt-0.5" />
                    <span>Ubegrenset notatgenerering</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="mr-2 h-4 w-4 text-green-600 mt-0.5" />
                    <span>Tilgang til alle funksjoner</span>
                  </li>
                </ul>
              </div>
            </TabsContent>
          </Tabs>
          
          <form onSubmit={handleRegister} className="space-y-4">
            {error && (
              <div className={componentStyles.alert.error + " flex items-center space-x-2 text-sm"}>
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="username" className={typography.label}>
                Brukernavn
              </Label>
              <div className="relative">
                <div className={componentStyles.input.icon.wrapper}>
                  <User className={componentStyles.input.icon.base} />
                </div>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={cn(
                    componentStyles.input.base,
                    componentStyles.input.withIcon
                  )}
                  placeholder="Velg et brukernavn"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className={typography.label}>
                E-post
              </Label>
              <div className="relative">
                <div className={componentStyles.input.icon.wrapper}>
                  <Mail className={componentStyles.input.icon.base} />
                </div>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={cn(
                    componentStyles.input.base,
                    componentStyles.input.withIcon
                  )}
                  placeholder="din@epost.no"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className={typography.label}>
                Passord
              </Label>
              <div className="relative">
                <div className={componentStyles.input.icon.wrapper}>
                  <Lock className={componentStyles.input.icon.base} />
                </div>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={cn(
                    componentStyles.input.base,
                    componentStyles.input.withIcon
                  )}
                  placeholder="Minst 8 tegn"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className={typography.label}>
                Bekreft passord
              </Label>
              <div className="relative">
                <div className={componentStyles.input.icon.wrapper}>
                  <Lock className={componentStyles.input.icon.base} />
                </div>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={cn(
                    componentStyles.input.base,
                    componentStyles.input.withIcon
                  )}
                  placeholder="Gjenta passord"
                  required
                />
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={isLoading}
              className={cn(
                componentStyles.button.base,
                componentStyles.button.primary,
                componentStyles.button.sizes.md,
                "w-full",
                isLoading && componentStyles.button.states.loading
              )}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className={cn(
                    "mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full",
                    effects.spin
                  )}></div>
                  Registrerer...
                </div>
              ) : (
                "Opprett konto"
              )}
            </Button>
            
            <div className="text-center mt-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Har du allerede en konto?{' '}
                <button 
                  type="button"
                  onClick={onSwitchToLogin}
                  className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                >
                  Logg inn
                </button>
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Registration;